import axios from '@/plugins/axios';
import store from '@/stores/store';
import qs from 'qs';
import utils from '@/stores/utils';
import $utils from '@/utils';
import { CSV_EXPORT_LIMIT } from '@/constants/csv-export';

const { getStorageKey, loadFiltersFromStorage, saveFiltersToStorage } = utils;

const DEFAULT_FILTERS = { offset: 0, limit: 20 };

const createState = (options = {}) => {
    return {
        allocations: [],
        allocations_filters: DEFAULT_FILTERS,
        allocations_count: 0,
        allocations_loaded: false,
        allocations_loading: false,
        allocations_loading_errors: null,
        
        allocations_stats: {},
        allocations_stats_loaded: false,
        allocations_stats_loading: false,
        allocations_stats_loading_errors: null,

        allocation_deleting: false,
        allocation_deleting_errors: null,
        cancelTokenSource: null,
        cancelTokenSourceStats: null,
        
        _storageOptions: options || {},
    };
};


const mutations = {
    updateAllocations(state, allocations) {
        state.allocations = allocations;
        state.allocations_loaded = true;
    },
    updateAllocationsCount(state, count) {
        state.allocations_count = count;
    },
    updateAllocationsFilters(state, filters) {
        state.allocations_filters = filters;
        const { storageKey, fieldsToSave } = state._storageOptions;
        if (storageKey) {
            saveFiltersToStorage(storageKey, state.allocations_filters, fieldsToSave);
        }
    },
    updateAllocationsLoading(state, loading) {
        state.allocations_loading = loading;
    },
    updateAllocationsLoadingErrors(state, errors) {
        state.allocations_loading_errors = errors;
    },

    updateAllocationsStats(state, allocations_stats) {
        state.allocations_stats = allocations_stats;
        state.allocations_stats_loaded = true;
    },
    updateAllocationsStatsLoading(state, loading) {
        state.allocations_stats_loading = loading;
    },
    updateAllocationsStatsLoadingErrors(state, errors) {
        state.allocations_stats_loading_errors = errors;
    },

    updateAllocationDeleting(state, deleting) {
        state.allocation_deleting = deleting;
    },
    updateAllocationDeletingErrors(state, errors) {
        state.allocation_deleting_errors = errors;
    },
    updateAllocationsLoaded(state, loaded) {
        state.allocations_loaded = loaded;
    },
    updateCancelTokenSource(state, source) {
        state.cancelTokenSource = source;
    },
    updateCancelTokenSourceStats(state, source) {
        state.cancelTokenSourceStats = source;
    },
    updateStorageOptions(state, storage_options) {
        state._storageOptions = storage_options;
    },
};

const actions = {

    exportAllocationsAsCSV({ commit, dispatch, state }, params) {
        let filters = {};
        if (params.filtered) {
            filters = Object.assign({}, state.allocations_filters);
            filters.offset = null;
        }
        filters.format = 'csv';
        filters.limit = CSV_EXPORT_LIMIT;
        
        $utils.downloadFile({
            url: "/api/allocations/",
            params: filters,
        }, $utils.getFilename('allocations'));
    },

    async deleteAllocation({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("order.delete_allocation") == -1) {
            return;
        }

        const url = `/api/allocations/${params.instance.id}/`;

        commit('updateAllocationDeleting', true);
        commit('updateAllocationDeletingErrors', null);

        try {
            const response = await axios.delete(url);
            commit('updateAllocationDeleting', false);
            return response;
        } catch (xhr_error) {
            const error = utils.handleError(xhr_error);
            commit('updateAllocationDeleting', false);
            commit('updateAllocationDeletingErrors', error.details);
            throw error;
        }
    },

    async fetchAllocations({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("order.view_allocation") == -1) {
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSource) {
            state.cancelTokenSource.cancel("New fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSource', source);

        commit('updateAllocationsLoading', true);
        commit('updateAllocationsLoadingErrors', null);
        commit('updateAllocationsStatsLoading', true);

        try {
            const response = await axios.get('/api/allocations/', {
                params: state.allocations_filters,
                cancelToken: source.token,
            });
            commit('updateAllocationsLoading', false);
            commit('updateAllocations', response.data.results);
            commit('updateAllocationsCount', response.data.count);
            dispatch('fetchAllocationsStats');
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateAllocationsLoadingErrors', error.details);
            commit('updateAllocationsLoading', false);
            throw error;
        }
    },

    async fetchAllocationsStats({ commit, dispatch, state }, params) {
        if (store.getters['session/current_user_permissions'].indexOf("order.view_allocation") == -1) {
            commit('updateAllocationsStatsLoading', false);
            return;
        }

        // Cancel any pending request
        if (state.cancelTokenSourceStats) {
            state.cancelTokenSourceStats.cancel("New stats fetch started");
        }

        // Create new cancel token source
        const source = axios.CancelToken.source();
        commit('updateCancelTokenSourceStats', source);

        commit('updateAllocationsStatsLoading', true);
        commit('updateAllocationsStatsLoadingErrors', null);

        try {
            const response = await axios.get('/api/allocations/stats/', {
                params: state.allocations_filters,
                cancelToken: source.token,
            });
            commit('updateAllocationsStatsLoading', false);
            commit('updateAllocationsStats', response.data);
            return response;
        } catch (xhr_error) {
            // Ignore cancelled requests
            if (axios.isCancel(xhr_error)) {
                return;
            }
            const error = utils.handleError(xhr_error);
            commit('updateAllocationsStatsLoadingErrors', error.details);
            commit('updateAllocationsStatsLoading', false);
            throw error;
        }
    },

    async init({ commit, dispatch, state }, params) {
        commit('updateAllocationsLoaded', false);
        commit('updateAllocations', []);
        commit('updateAllocationsCount', 0);
        
        // Update storage options if provided
        if (params?.storageOptions !== undefined) {
            await commit('updateStorageOptions', params.storageOptions);
        }
        
        // Load filters from localStorage if storage is configured
        await commit('updateAllocationsFilters', { 
            ...DEFAULT_FILTERS, 
            ...(state._storageOptions?.storageKey ? loadFiltersFromStorage(state._storageOptions?.storageKey, {}) : {}),
            ...(params?.filters || {})
        });
    }
};

export default (options = {}) => ({
    namespaced: true,
    state: createState(options),
    mutations,
    actions
});
